import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Play } from "lucide-react";
import { ASSETS } from "@/data/assets";
import api from "@/lib/apiClient";

export default function Gallery() {
  const { data: items = [], isLoading } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="gallery-page">
      <section className="relative h-[40vh] min-h-[300px] flex items-end">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/haldi.webp`} alt="Live wedding celebration by WEDDING EVENTS" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-12 w-full">
          <p className="overline mb-3">Our Work</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">Gallery</h1>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap gap-3 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-sm rounded-sm border transition-colors ${filter === c ? "bg-brand-navy text-white border-brand-navy" : "border-brand-navy/20 text-brand-ink/70 hover:border-brand-gold"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-brand-ink/60">Loading gallery…</p>}
        {!isLoading && filtered.length === 0 && <p className="text-brand-ink/60">No items in this category yet.</p>}

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
          {filtered.map((item) => (
            <button
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              onClick={() => setLightbox(item)}
              className="group relative block w-full overflow-hidden rounded-sm break-inside-avoid"
            >
              {item.type === "video" ? (
                <>
                  <img src={item.poster || item.url} alt={item.title} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <div className="w-14 h-14 rounded-full bg-brand-gold/90 grid place-items-center shadow-lg">
                      <Play className="text-brand-navy ml-0.5" size={24} fill="currentColor" />
                    </div>
                  </div>
                </>
              ) : (
                <img src={item.url} alt={item.title} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                <div className="text-left">
                  <p className="overline">{item.category}</p>
                  <p className="font-serif text-white text-lg mt-1">{item.title}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {lightbox && (
        <div data-testid="gallery-lightbox" onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-brand-navy/95 grid place-items-center p-5 cursor-zoom-out">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "video" ? (
              <video src={lightbox.url} poster={lightbox.poster} controls autoPlay playsInline className="w-full max-h-[80vh] object-contain rounded-sm bg-black" />
            ) : (
              <img src={lightbox.url} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-sm" />
            )}
            <p className="text-center text-white/80 mt-4 font-serif text-lg">{lightbox.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
